import {
  ConfirmDestructiveButton,
  FieldSelect,
  Form,
  FormApplyOrCancel,
  FormikForm,
  FormRowField,
  Loading,
} from "@skilitics/designer-ui-components";
import { useFormatMessage } from "@skilitics/localization";
import type { ContentLanguage, Language } from "@skilitics/nautilus-core-designer-data";

export type AddNewLanguageFormValues = {
  code: string;
  baseCode: string;
};

type EditContentLanguageProps = {
  activeLanguage: string;
  availableLanguages: Language[];
  contentLanguage: ContentLanguage;
  contentLanguages: ContentLanguage[];
  onCancel: () => void;
  onRemove?: (code: string) => void;
  onSubmit: (formValues: AddNewLanguageFormValues) => void;
  primaryLanguageCode: string;
};

export const EditContentLanguage = ({
  activeLanguage,
  availableLanguages,
  contentLanguage,
  contentLanguages,
  onCancel,
  onRemove,
  onSubmit,
  primaryLanguageCode,
}: EditContentLanguageProps) => {
  const formatMessage = useFormatMessage(languageFormFormatStrings);

  if (!availableLanguages.length) {
    return <Loading />;
  }

  const availableOptions = availableLanguages.map(({ code, title }) => ({
    label: `${code} - ${title}`,
    value: code,
    disabled: !!contentLanguages.find((language) => language.code === code),
  }));

  const baseOptions = [
    {
      label: formatMessage("noBaseLanguage"),
      value: "none",
    },
    ...contentLanguages
      .filter(({ code }) => code !== contentLanguage.code)
      .map(({ language: { code, title } }) => ({
        label: `${code} - ${title}`,
        value: code,
      })),
  ];

  // Rules:
  // - Cannot remove primary language.
  // - Cannot remove language if it is the base for another language.
  // - Cannot remove active language.
  const isRemoveDisabled =
    !onRemove ||
    primaryLanguageCode === contentLanguage.language.code ||
    activeLanguage === contentLanguage.code ||
    !!contentLanguages.find((cl) => cl.baseCode && cl.baseCode === contentLanguage.code);

  return (
    <div>
      <FormikForm<AddNewLanguageFormValues>
        initialValues={{ code: contentLanguage.code, baseCode: contentLanguage.baseCode ?? "none" }}
        onSubmit={onSubmit}
        onReset={onCancel}
        formatStrings={languageFormFormatStrings}
        dataTest="languages-form"
      >
        <Form>
          <FormRowField<AddNewLanguageFormValues> name="code">
            <FieldSelect options={availableOptions} disabled />
          </FormRowField>
          <FormRowField<AddNewLanguageFormValues> name="baseCode">
            <FieldSelect options={baseOptions} disabled={primaryLanguageCode === contentLanguage.language.code} />
          </FormRowField>
          <FormApplyOrCancel
            applyLabel={formatMessage("editSubmitButtonLabel")}
            alwaysShow
            destroyButton={
              <ConfirmDestructiveButton
                header="Remove Language from Module"
                message="Are you sure you want to remove this language from the module? Any translations will be lost."
                onDestroy={() => onRemove?.(contentLanguage.code)}
                label="Remove"
                disabled={isRemoveDisabled}
              />
            }
          />
        </Form>
      </FormikForm>
    </div>
  );
};

export const languageFormFormatStrings = {
  codeLabel: {
    en: "Select Language",
  },
  codeDescription: {
    en: "Select an additional language to support.",
  },
  baseCodeLabel: {
    en: "Fallback Language",
  },
  baseCodeDescription: {
    en: "Content will fallback to this language if the selected language is not available.",
  },
  noBaseLanguage: {
    en: "No fallback language",
  },
  addSubmitButtonLabel: {
    en: "Add Language",
  },
  editSubmitButtonLabel: {
    en: "Save",
  },
};
